import {
  DocumentDefinition,
  QuoteDocumentConfig,
  UpdateQuoteDocument,
} from '@monetize/types/app';
import { MimeTypeEnum } from '@monetize/types/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPut, apiUpload } from './axios';
import { ApiQueryItem } from './queryUtils';
import { settingsQueryKeys } from './settingsService';
import { asQueryUtil } from './utils';

/**
 * Legal Entities
 */
export const legalEntitiesServiceQueryKeys = {
  base: ['legalEntities'] as const,
  legalEntityList: () =>
    [...legalEntitiesServiceQueryKeys.base, 'list'] as const,
  legalEntityDetail: (id: string) =>
    [...legalEntitiesServiceQueryKeys.base, id] as const,
};

const legalEntities: ApiQueryItem = {
  list: {
    endpoint: () => `/api/configurations/legalEntities`,
    queryKey: () => legalEntitiesServiceQueryKeys.legalEntityList(),
    byIdQueryKey: (id: string) =>
      legalEntitiesServiceQueryKeys.legalEntityDetail(id),
  },
  byId: {
    endpoint: (id: string) => `/api/configurations/legalEntities/${id}`,
    queryKey: (id: string) =>
      legalEntitiesServiceQueryKeys.legalEntityDetail(id),
  },
  create: {
    endpoint: () => `/api/configurations/legalEntities`,
    invalidateKeys: (id: string) => [
      legalEntitiesServiceQueryKeys.legalEntityList(),
    ],
    setDataKey: (id: string) =>
      legalEntitiesServiceQueryKeys.legalEntityDetail(id),
  },
  update: {
    endpoint: (id) => `/api/configurations/legalEntities/${id}`,
    invalidateKeys: (id: string) => [
      legalEntitiesServiceQueryKeys.legalEntityList(),
    ],
    setDataKey: (id: string) =>
      legalEntitiesServiceQueryKeys.legalEntityDetail(id),
  },
  delete: {
    endpoint: (id) => `/api/configurations/legalEntities/${id}`,
    invalidateKeys: (id: string) => [
      legalEntitiesServiceQueryKeys.legalEntityList(),
    ],
    setDataKey: (id: string) =>
      legalEntitiesServiceQueryKeys.legalEntityDetail(id),
  },
};

/**
 * Storage by Legal Entities
 */
export const storageByLegalEntityServiceQueryKeys = {
  base: ['storageByLegalEntity'] as const,
  legalEntityStorageDetail: (id: string) =>
    [...storageByLegalEntityServiceQueryKeys.base, id] as const,
};

const storageByLegalEntity: Required<
  Omit<ApiQueryItem, 'create' | 'update' | 'list'>
> = {
  byId: {
    endpoint: (id: string) => `/api/configurations/legalEntities/${id}/logo`,
    queryKey: (id: string) =>
      storageByLegalEntityServiceQueryKeys.legalEntityStorageDetail(id),
  },
  upload: {
    endpoint: (id) => `/api/configurations/legalEntities/${id}/logo`,
    invalidateKeys: (id: string) => [
      storageByLegalEntityServiceQueryKeys.legalEntityStorageDetail(id),
    ],
    setDataKey: (id: string) =>
      storageByLegalEntityServiceQueryKeys.legalEntityStorageDetail(id),
  },
  delete: {
    endpoint: (id) => `/api/configurations/legalEntities/${id}/logo`,
    invalidateKeys: (id: string) => [
      storageByLegalEntityServiceQueryKeys.legalEntityStorageDetail(id),
    ],
    setDataKey: (id: string) =>
      storageByLegalEntityServiceQueryKeys.legalEntityStorageDetail(id),
  },
};

export const LEGAL_ENTITY_SERVICE_API = asQueryUtil({
  legalEntities,
  storageByLegalEntity,
});

export const doGetDefaultLegalEntity = async () => {
  const res = await apiGet(
    '/api/configurations/legalEntities?defaultEntity=true',
  );
  return res.data;
};

/**
 * Legal Entity Quote Document Definitions
 */
export const legalEntityQuoteDocumentDefinitionsQueryKeys = {
  base: ['legalEntityQuoteDocumentDefinitions'] as const,
  byLegalEntityId: (legalEntityId: string) =>
    [
      ...legalEntityQuoteDocumentDefinitionsQueryKeys.base,
      legalEntityId,
    ] as const,
  documentConfig: (documentDefinitionId: string, documentConfigId: string) =>
    [
      ...legalEntityQuoteDocumentDefinitionsQueryKeys.base,
      'documentConfig',
      documentDefinitionId,
      documentConfigId,
    ] as const,
};

export const useGetLegalEntityQuoteDocumentDefinitions = (
  legalEntityId: string,
) => {
  return useQuery({
    queryKey:
      legalEntityQuoteDocumentDefinitionsQueryKeys.byLegalEntityId(
        legalEntityId,
      ),
    queryFn: async (): Promise<DocumentDefinition> => {
      const res = await apiGet(
        `/api/legalEntities/${legalEntityId}/documentDefinitions/quotes`,
      );
      return res.data;
    },
    enabled: !!legalEntityId,
  });
};

export const useDeleteDocumentConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentDefinitionId,
      documentConfigId,
    }: {
      documentDefinitionId: string;
      documentConfigId: string;
    }) => {
      const res = await apiDelete(
        `/api/documentDefinitions/${documentDefinitionId}/documentConfigs/${documentConfigId}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: legalEntityQuoteDocumentDefinitionsQueryKeys.base,
      });
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteDocumentDefinitions(),
      });
    },
  });
};

export const useUploadQuoteDocument = (legalEntityId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await apiUpload(
        `/api/legalEntities/${legalEntityId}/documentDefinitions/quotes`,
        formData,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          legalEntityQuoteDocumentDefinitionsQueryKeys.byLegalEntityId(
            legalEntityId,
          ),
      });
    },
  });
};

export const useUpdateQuoteDocumentConfigurations = (
  documentDefinitionId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      configurations: UpdateQuoteDocument[],
    ): Promise<DocumentDefinition> => {
      const res = await apiPut(
        `/api/documentDefinitions/${documentDefinitionId}/quote`,
        configurations,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: legalEntityQuoteDocumentDefinitionsQueryKeys.base,
      });
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteDocumentDefinitions(),
      });
    },
  });
};

export const doGetQuoteDocumentConfig = async (
  documentDefinitionId: string,
  documentConfigId: string,
  attachContent = true,
  mediaType: MimeTypeEnum = MimeTypeEnum.PDF,
): Promise<QuoteDocumentConfig> => {
  const res = await apiGet(
    `/api/documentDefinitions/${documentDefinitionId}/documentConfigs/${documentConfigId}`,
    {
      params: {
        attachContent,
        mediaType,
      },
    },
  );
  return res.data;
};
